/**
 * Production-safe logging system
 * Replaces console.log calls with appropriate logging levels
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  userId?: string;
  campaignId?: string;
  referentId?: string;
  action?: string;
  [key: string]: any;
}

class ProductionLogger {
  private isDevelopment = import.meta.env.DEV;
  private logLevel: LogLevel = this.isDevelopment ? 'debug' : 'warn'; // ✅ WARN en prod, DEBUG en dev

  private shouldLog(level: LogLevel): boolean {
    const levels = ['debug', 'info', 'warn', 'error'];
    return levels.indexOf(level) >= levels.indexOf(this.logLevel);
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` | Context: ${JSON.stringify(context)}` : '';
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`;
  }

  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment && this.shouldLog('debug')) {
      console.debug(this.formatMessage('debug', message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog('info')) {
      if (this.isDevelopment) {
        console.info(this.formatMessage('info', message, context));
      } else {
        // In production, could send to analytics service
        // analytics.track('info_log', { message, context });
      }
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage('warn', message, context));
      // In production, could send to monitoring service
      // monitoring.warn(message, context);
    }
  }

  error(message: string, error?: Error, context?: LogContext): void {
    if (this.shouldLog('error')) {
      const errorContext = error ? { ...context, error: error.message, stack: error.stack } : context;
      console.error(this.formatMessage('error', message, errorContext));
      // In production, should send to error tracking service
      // errorTracking.captureException(error || new Error(message), context);
    }
  }

  // Campaign-specific logging
  campaignAction(action: string, campaignId: string, details?: any): void {
    this.info(`Campaign ${action}`, { campaignId, action, ...details });
  }

  // Referent-specific logging
  referentAction(action: string, referentId: string, campaignId?: string, details?: any): void {
    this.info(`Referent ${action}`, { referentId, campaignId, action, ...details });
  }

  // Performance logging
  performance(operation: string, duration: number, context?: LogContext): void {
    this.debug(`Performance: ${operation} took ${duration}ms`, context);
  }
}

export const logger = new ProductionLogger();
export default logger;